import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RevealGroup, RevealItem } from '../Animate/reveal-component';
import Image from '../Image/image-component';
import { getLandingData } from '../../Public/landing-data';

// Generic placeholder when a step doesn't carry its own image (content/S3).
const FALLBACK_IMAGE = '/placeholder.svg';

const DEFAULT_STEPS_HE = [
    { title: 'אפיון', desc: 'הבנת הצרכים העסקיים והטכנולוגיים ותרגומם לדרישות מערכת מדויקות.' },
    { title: 'תכנון', desc: 'בניית ארכיטקטורה ותהליכים חכמים שמבטיחים סקיילביליות ויציבות לאורך זמן.' },
    { title: 'הטמעה', desc: 'יישום המערכת בפועל תוך חיבור מלא לתהליכים הארגוניים והטכנולוגיים.' },
    { title: 'ליווי', desc: 'אופטימיזציה מתמשכת, שיפורים ותמיכה שוטפת לצמיחה והתייעלות.' },
];

const DEFAULT_STEPS_EN = [
    { title: 'Discovery', desc: 'Understanding your business and technical needs, translated into precise system requirements.' },
    { title: 'Planning', desc: 'Building smart architecture and workflows that ensure scalability and long-term stability.' },
    { title: 'Implementation', desc: 'Deploying the system and fully integrating it with your organizational and technical processes.' },
    { title: 'Support', desc: 'Ongoing optimization, improvements, and continuous support for growth and efficiency.' },
];

// The slanted parallelogram mask for each image tile. Same skew on every tile so
// the gap reads as angled white slivers between them (the interlocking look).
// Mirrored horizontally for RTL so the lean follows reading direction.
const CLIP_LTR = 'polygon(20% 0, 100% 0, 80% 100%, 0 100%)';
const CLIP_RTL = 'polygon(0 0, 80% 0, 100% 100%, 20% 100%)';

/**
 * Process, "Our Process" section. A left-aligned heading + subtitle with an
 * optional right-aligned eyebrow stack, above a row of slanted parallelogram
 * image tiles, each captioned with a step title + description.
 * Desktop/tablet (sm+): a static grid of tiles. Mobile (<sm): a horizontal
 * swipe carousel of the same tiles with dot indicators.
 * Purely presentational; content is data-driven.
 * Props: { title, subtitle, eyebrows:[string], steps:[{title, desc, image}] }.
 */
function ProcessSection({ title, subtitle, eyebrows, steps }) {
    const { language = 'he', dir = 'rtl' } = useSelector((s) => s.systemData || {});
    const isRtl = dir === 'rtl';

    const DEFAULT_STEPS = language === 'en' ? DEFAULT_STEPS_EN : DEFAULT_STEPS_HE;
    // Fallback source: the landing "Our Process" steps carry real images, so a
    // section with steps but no images (e.g. service pages) borrows them by index.
    const landingSteps = getLandingData(language)?.processSection?.steps || [];
    const STEPS = (steps && steps.length > 0)
        ? steps.map((s, i) => ({
            title: s.title || landingSteps[i]?.title || '',
            desc: s.desc || s.content || s.description || landingSteps[i]?.desc || '',
            image: s.image || s.mobileImage || landingSteps[i]?.image || FALLBACK_IMAGE,
        }))
        : (landingSteps.length
            ? landingSteps.map((s) => ({ title: s.title, desc: s.desc, image: s.image || FALLBACK_IMAGE }))
            : DEFAULT_STEPS.map((s) => ({ ...s, image: FALLBACK_IMAGE })));

    const sectionTitle = title || (language === 'en' ? 'Our Process' : 'התהליך שלנו');
    const clip = isRtl ? CLIP_RTL : CLIP_LTR;
    const eyebrowLines = Array.isArray(eyebrows) ? eyebrows.filter(Boolean) : [];

    // Mobile carousel: track which tile is centered (for the dot indicator).
    const trackRef = useRef(null);
    const [active, setActive] = useState(0);

    // Nearest-to-center child wins, uses viewport rects so it's sign-agnostic
    // (works for both LTR and RTL scroll directions).
    const onScroll = () => {
        const el = trackRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        let best = 0;
        let bestDist = Infinity;
        Array.from(el.children).forEach((child, i) => {
            const cr = child.getBoundingClientRect();
            const dist = Math.abs(cr.left + cr.width / 2 - center);
            if (dist < bestDist) { bestDist = dist; best = i; }
        });
        setActive(best);
    };

    const goTo = (i) => {
        const el = trackRef.current;
        if (!el) return;
        const child = el.children[i];
        if (child) child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    const tileCaption = (step) => (
        <div className="mt-5 text-start">
            <h3 className="font-secondary text-lg font-bold leading-snug text-primary-500 sm:text-xl">
                {step.title}
            </h3>
            <p className="mt-2 text-base leading-relaxed text-primary-300 sm:mt-3">
                {step.desc}
            </p>
        </div>
    );

    return (
        <section dir={dir} className="w-full bg-[#F2F3F4] py-[72px] px-4 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-[1400px]">
                {/* Header: heading + subtitle on the start side, eyebrow stack on the end side */}
                <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                    <div className="max-w-2xl text-start">
                        <h2 className="font-secondary text-[40px] sm:text-[56px] font-bold leading-[1.05] tracking-[-0.03em] text-primary-500">
                            {sectionTitle}
                        </h2>
                        {subtitle && (
                            <p className="mt-4 text-lg sm:text-xl leading-relaxed text-primary-300">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    {eyebrowLines.length > 0 && (
                        <div className="shrink-0 text-start md:text-end">
                            {eyebrowLines.map((line, i) => (
                                <div
                                    key={i}
                                    className="font-secondary text-xs sm:text-sm font-medium uppercase tracking-[0.35em] text-primary-200"
                                >
                                    {line}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Desktop / tablet (sm+): static grid of slanted parallelogram tiles */}
                <RevealGroup className="mt-12 hidden gap-x-5 gap-y-10 sm:grid sm:grid-cols-2 lg:grid-cols-4">
                    {STEPS.map((step, i) => (
                        <RevealItem key={i}>
                            <div className="group flex h-full flex-col">
                                <div
                                    className="aspect-[4/5] w-full overflow-hidden bg-primary-100"
                                    style={{ clipPath: clip, WebkitClipPath: clip }}
                                >
                                    <Image
                                        src={step.image}
                                        alt={step.title}
                                        width={600}
                                        objectFit="cover"
                                        useSekeleton={false}
                                        wrapperClassName="h-full w-full"
                                        className="transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                                    />
                                </div>
                                {tileCaption(step)}
                            </div>
                        </RevealItem>
                    ))}
                </RevealGroup>

                {/* Mobile (<sm): horizontal swipe carousel of the same tiles + dots */}
                <div className="sm:hidden">
                    <div
                        ref={trackRef}
                        onScroll={onScroll}
                        className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth scroll-px-1 px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {STEPS.map((step, i) => (
                            <div key={i} className="w-[82%] shrink-0 snap-start">
                                <div
                                    className="aspect-[4/5] w-full overflow-hidden bg-primary-100"
                                    style={{ clipPath: clip, WebkitClipPath: clip }}
                                >
                                    <Image
                                        src={step.image}
                                        alt={step.title}
                                        width={600}
                                        objectFit="cover"
                                        useSekeleton={false}
                                        wrapperClassName="h-full w-full"
                                    />
                                </div>
                                {tileCaption(step)}
                            </div>
                        ))}
                    </div>

                    {/* Dot indicators, active dot stretches into a pill */}
                    <div className="mt-6 flex justify-center gap-2">
                        {STEPS.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => goTo(i)}
                                aria-label={`Go to step ${i + 1}`}
                                className={`h-2 rounded-full transition-all duration-300 ${i === active ? 'w-6 bg-primary-500' : 'w-2 bg-primary-200'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ProcessSection;
